import { Permissions } from '../../../../models/enums/permissions';
import cloneDeep from 'lodash/cloneDeep';
import * as React from 'react';
import { DataGroupHeader } from '../../../../components/data-group/DataGroupHeader';
import ResultDialog from '../../../../components/dialog-types/ResultDialog';
import * as Forms from '../../../../components/form-elements/_index';
import { PageBody, PageContent, PageHeading } from '../../../../components/page-components/_index';
import { useRouteMiss } from '../../../../hooks/use-route-miss';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { toSlashedShortDate } from '../../../../utils/date-utils';
import { history, navigateToPage } from '../../../../utils/history';
import * as transportActions from '../../../../transportation/actions';
import { ActionEditContainerInfo } from '../components/ActionEditContainerInfo';
import { ActionSetContainerCustoms } from '../components/ActionSetContainerCustoms';
import { EditContainerSidebar } from '../components/EditContainerSidebar';
import { EditShipmentActionList } from '../../../../transportation/components/shipments/EditShipmentActionList';
import { TransportationUtils } from '../../../../transportation/helpers';
import * as Containers from '../../../../transportation/models/containers/_index';
import { RouteStatus } from '../../../../transportation/models/enums/_index';
import { ContainerPaymentTypeMetadata } from '../../../../transportation/models/enums/container-payment';
import { ContainerSizeMetadata } from '../../../../transportation/models/enums/container-size';
import { DeliveryStatus } from '../../../../transportation/models/enums/delivery-status';
import { ShipmentActionType } from '../../../../transportation/models/enums/shipment-action-type';
import { DeliveryShipmentAction, ShipmentAction } from '../../../../transportation/models/shipments/shipment-action';
import { carrierMap } from '../../../../transportation/selectors';
import { TransportationSvc } from '../../../../transportation/service';
import '../../../../transportation/styles.scss';
import { ShipmentType } from '../../../../transportation/models/enums/shipment-type';

interface IProps {
    location: any;
    match: any;
}

export const EditContainerScene = (props: IProps) => {

    const [service]                                 = React.useState(new TransportationSvc());
    const [container, setContainer]                 = React.useState({} as Containers.ContainerRead);
    const [editContainer, setEditContainer]         = React.useState({} as Containers.ContainerRead);
    const [showInfoFlyout, setShowInfoFlyout]       = React.useState(false);
    const [showCustomsFlyout, setShowCustomsFlyout] = React.useState(false);
            
    useRouteMiss(
        Routes.SHIPPING_CONTAINERS,
        transportActions.clearContainerSearch()
    );

    /**
     * Go fetch the provided container.  If no ID is passed in
     * new up a ContainerRead for editing purposes
     * @param containerId The optional container to go fetch
     */
    async function getContainer(containerId: number) {
        if (containerId) {
            const result = await service.getContainer(containerId);
            if (result.status === StatusCode.Success) {
                setContainer(result.data);
                return;
            }
        }
        else {
            history.goBack();
        }
    }

    /**
     * Respond to changes in the location
     */
    React.useEffect(() => {
        const opts = props.location.pathname.split(Routes.SHIPPING_CONTAINER_EDIT + '/');
        getContainer(Number.parseInt(opts[1], 10));
    }, [props.location]);

    /**
     * Delete button click handler
     */
    const onDelete = async () => {
        // We need an ID and no stops can be invoiced
        if (container.id && !TransportationUtils.hasDeliveredStop(container)) {

            const result = await ResultDialog.show({
                title: 'Delete?',
                message: 'Are you sure you want to delete this container?'
            });

            if (result) {                
                await service.deleteRoute(container.id, container.rowVersion, container.type);
            }
        }
    };

    /**
     * Move the containers's status to the next state.
     * Open -> Ready to Ship -> In Transit -> Closed
     */
    const updateContainerStatus = async () => {
        const result = await service.updateContainerStatus(container);
        if (result.status === StatusCode.Success) {
            setContainer(result.data);
        }
    };

    /**
     * Mark the provided stop as delivered and update the truck state
     * @param stop The delivery to mark as delivered
     */
    const markStopDelivered = async (stop : ShipmentAction) => {     
        const result = await service.markStopDelivered(stop, container.id, container.rowVersion, ShipmentType.Container);
        if (result.status === StatusCode.Success) {
            const c = {...container} as Containers.ContainerRead;
            c.rowVersion = result.data;
            c.actions
                .filter((s : ShipmentAction) => s.type === ShipmentActionType.Delivery )
                .forEach((cd: DeliveryShipmentAction) => {
                    if (cd.id === stop.id) {
                        cd.status = DeliveryStatus.Completed;
                    }
                });

            setContainer(c);
        }
    };

    const deleteStop = async (stopId : number) => {

        const result = await service.deleteStopFromRoute(
            stopId,
            container.id,
            container.rowVersion,
            ShipmentType.Container
        );

        if (result.status === StatusCode.Success) {
            await refreshContainer();
        }

        return result.status;
    };

    const refreshContainer = async () => {
        const result = await service.getContainer(container.id);
        if (result.status === StatusCode.Success) {
            setContainer(result.data);
        }
    };

    /**
     * Basic Info Flyout
     */    
    const showEditBasicInfo = () => {
        const t = cloneDeep(container);
        setEditContainer(t);
        setShowInfoFlyout(true);
    };

    const hideEditBasicInfo = () => {
        setEditContainer({} as unknown as Containers.ContainerRead);
        setShowInfoFlyout(false);
    };

    const onSetBasicInfoSuccess = () => {
        hideEditBasicInfo();
        refreshContainer();
    };
    
    /**
     * Customs Flyout
     */
    const showContainerCustoms = () => {
        const containerCopy = cloneDeep(container) as Containers.ContainerRead;
        setEditContainer(containerCopy);
        setShowCustomsFlyout(true);
    };

    const hideContainerCustoms = () => {
        setEditContainer({} as unknown as Containers.ContainerRead);
        setShowCustomsFlyout(false);
    };

    const onSetCustomsSuccess = () => {
        setEditContainer({} as unknown as Containers.ContainerRead);
        hideContainerCustoms();
        refreshContainer();
    };   

    return !AppUtils.IsEmpty(container) 
        ? <PageContent classes="edit-route" pageName={PageNames.TRANSPORTATION_CONTAINER_DETAIL}>
            
            <PageHeading 
                shouldStick={true} 
                showBackBtn={ true }
                onBackClick={ () => navigateToPage(Routes.SHIPPING_CONTAINERS) }
                title={ `${ container.name } [${ container.id }]` }>
                {
                    (container.status !== RouteStatus.Cancelled && container.status !== RouteStatus.Closed)
                        ? <Forms.PageActions
                                id="pickup-edit-form"
                                onEdit={ showEditBasicInfo }
                                editPermissions={[
                                    Permissions.ShippingContainersEdit
                                ]} 
                                onDelete={ onDelete }
                                onDeleteDisable={ TransportationUtils.hasDeliveredStop(container) }
                                deletePermissions={[
                                    Permissions.ShippingContainersDelete
                                ]} 
                                />
                        : null
                }
            </PageHeading>
            <PageBody>
                <div className="row">
                    <div className="col-9">

                        <DataGroupHeader title="Basic Info" />

                        <div className="row">
                            <div className="col-6">
                                <Forms.ReadView
                                    label="Name"
                                    value={ container.name }
                                />
                            </div>
                            <div className="col-6">
                                <Forms.ReadView
                                    label="Service Center"
                                    value={ container.serviceCenterId }
                                />
                            </div>
                        </div>

                        <div className="row">
                         <div className="col-6">
                                <Forms.ReadView
                                    label="Number"
                                    value={ container.number }
                                />
                            </div>
                            <div className="col-6">
                                <Forms.ReadView                                    
                                    label="Payment Type"                                    
                                    value={ container.paymentType !== undefined ? ContainerPaymentTypeMetadata[container.paymentType].display : 'Not Set' } 
                                />
                            </div>                
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <Forms.ReadView
                                    label="Carrier"
                                    value={ carrierMap(container.carrierId) }
                                />
                            </div>
                            <div className="col-6">
                                <Forms.ReadView
                                    label="Container Size"
                                    value={ container.size !== undefined ? ContainerSizeMetadata[container.size].display : 'Not Set' } 
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <Forms.ReadView
                                    label="Cutoff Date"
                                    value={ container.cutoffDate ? toSlashedShortDate(container.cutoffDate) : '' }
                                />
                            </div>
                        </div>

                        {
                            container.note && container.note.length > 0
                            ? <React.Fragment>
                                <DataGroupHeader title="Notes" />
                                <div className="row">
                                    <div className="col-12">
                                        <p>{ container.note }</p>
                                    </div>
                                </div>
                            </React.Fragment>
                            : null
                        }

                        <DataGroupHeader                         
                            title="Customs Information" 
                            actionClick={ showContainerCustoms }
                            disabled={ container.status === RouteStatus.Closed }
                            actionText="Edit"    
                            requiredPermissions={[
                                Permissions.ShippingContainersEdit
                            ]}
                        />

                        <div className="row">
                            <div className="col-4">                                
                                <Forms.ReadView
                                    label="Tare Weight"                                    
                                    value={ container.tareWeight === null ? '' : container.tareWeight }
                                />
                            </div>
                            <div className="col-4">
                                <Forms.ReadView                                    
                                    label="Unloaded Weight"                                    
                                    value={ container.unloadedWeight === null ? '' : container.unloadedWeight }
                                />
                            </div>
                            <div className="col-4">
                                <Forms.ReadView                                    
                                    label="Loaded Weight"                                    
                                    value={ container.loadedWeight === null ? '' : container.loadedWeight }
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-6">
                                <Forms.ReadView                                    
                                    label="Seal Number"                                    
                                    value={ container.seal === null ? '' : container.seal }                                    
                                />
                            </div>                                
                        </div>
                    </div>
                    
                    <EditContainerSidebar 
                        container={ container }
                        updateContainerState={ updateContainerStatus }
                    />
                    
                </div>

                <div className="row divider-row">
                    <div className="col-md-12">
                        <EditShipmentActionList
                            route={ container }
                            refreshRoute={ refreshContainer }
                            markStopDelivered={ markStopDelivered }
                            deleteStop={ deleteStop }
                        />
                    </div>
                </div>

                <ActionEditContainerInfo
                    visible={ showInfoFlyout }
                    onSuccess={ onSetBasicInfoSuccess }
                    onClose={ hideEditBasicInfo }
                    container={ editContainer }
                />

                <ActionSetContainerCustoms
                    visible={ showCustomsFlyout }
                    onSuccess={ onSetCustomsSuccess }
                    onClose={ hideContainerCustoms }
                    container={ editContainer }
                />
                    
            </PageBody>
        </PageContent>
        : null;
};
